import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import OwnerPersonSelect from "../components/OwnerPersonSelect";
import { toast } from "sonner";

const STATUS_OPTS = [
  { value: "not_started", label: "No iniciado" },
  { value: "in_progress", label: "En progreso" },
  { value: "implemented", label: "Implementado" },
  { value: "not_applicable", label: "No aplica" },
];

export default function ControlDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [impl, setImpl] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ status: "", applicability: "", justification: "", owner_user_id: "", owner_person_id: "", notes: "", last_review_date: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [impl, usersR] = await Promise.all([api.get(`/control-implementations/${id}`), api.get(`/users?page=1&page_size=100`)]);
      setImpl(impl.data);
      setUsers(usersR.data.items || []);
      setForm({
        status: impl.data.status || "not_started",
        applicability: impl.data.applicability || "applicable",
        justification: impl.data.applicability_justification || "",
        owner_user_id: impl.data.owner_user_id || "",
        owner_person_id: impl.data.owner_person_id || "",
        notes: impl.data.notes || "",
        last_review_date: impl.data.last_review_date ? impl.data.last_review_date.slice(0, 10) : "",
      });
    })();
  }, [id]);

  if (!impl) return <div className="text-slate-500">Cargando control...</div>;
  const ctrl = impl.control;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        applicability: form.applicability,
        applicability_justification: form.justification,
        owner_user_id: form.owner_user_id || null,
        owner_person_id: form.owner_person_id || null,
        notes: form.notes,
        last_review_date: form.last_review_date ? new Date(form.last_review_date).toISOString() : null,
      };
      const { data } = await api.put(`/control-implementations/${id}`, payload);
      setImpl({ ...impl, ...data });
      toast.success("Control actualizado.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const accept = async () => { setForm((f) => ({ ...f, applicability: impl.llm_suggestion?.suggested_applicability, justification: impl.llm_suggestion?.reasoning || "" })); toast.success("Sugerencia IA aplicada al formulario."); };
  const reject = async () => { setForm((f) => ({ ...f, applicability: "applicable", justification: "" })); toast.info("Sugerencia rechazada."); };

  return (
    <div data-testid="control-detail-page" className="max-w-3xl">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Volver</Button>
      <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">{ctrl?.code} · {ctrl?.title}</h1>
      <p className="text-[12px] text-slate-500 mt-1">Categoría: {ctrl?.category} · Tipo: {ctrl?.control_type}</p>

      <div className="mt-4 bg-white border border-slate-200 rounded-md p-5 text-[14px] text-slate-700">
        <div className="font-semibold text-slate-900 mb-1">Descripción</div>
        <p>{ctrl?.description}</p>
        {ctrl?.guidance && (<><div className="font-semibold text-slate-900 mt-3 mb-1">Guía</div><p>{ctrl.guidance}</p></>)}
      </div>

      {impl.applicability_source === "llm_suggested" && impl.llm_suggestion && (
        <div className="mt-4 border border-primary/30 bg-primary/5 rounded-md p-4" data-testid="llm-suggestion-box">
          <div className="flex items-center gap-2 text-primary text-[14px] font-semibold"><Sparkles className="h-4 w-4" /> Sugerencia de la IA</div>
          <p className="text-[14px] text-slate-700 mt-2">{impl.llm_suggestion.reasoning}</p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={accept} data-testid="llm-accept">Aceptar sugerencia</Button>
            <Button size="sm" variant="outline" onClick={reject} data-testid="llm-reject">Rechazar y marcar como aplicable</Button>
          </div>
        </div>
      )}

      <div className="mt-4 bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <h2 className="text-[16px] font-semibold text-slate-900">Implementación en tu organización</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Estado</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="ctrl-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Aplicabilidad</Label>
            <Select value={form.applicability} onValueChange={(v) => setForm({ ...form, applicability: v })}>
              <SelectTrigger data-testid="ctrl-applicability"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="applicable">Aplicable</SelectItem>
                <SelectItem value="not_applicable">No aplicable</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {form.applicability === "not_applicable" && (
          <div>
            <Label>Justificación (requerida si no aplica)</Label>
            <Textarea data-testid="ctrl-justification" value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} rows={3} />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Responsable (persona)</Label>
            <OwnerPersonSelect
              value={form.owner_person_id}
              onChange={(v) => setForm({ ...form, owner_person_id: v })}
              controlCategory={ctrl?.category}
              legacyUserLabel={!form.owner_person_id && form.owner_user_id ? users.find((u) => u.id === form.owner_user_id)?.name : null}
              testid="ctrl-owner"
            />
          </div>
          <div>
            <Label>Última revisión</Label>
            <Input type="date" data-testid="ctrl-review" value={form.last_review_date} onChange={(e) => setForm({ ...form, last_review_date: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Notas</Label>
          <Textarea data-testid="ctrl-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
        </div>
        <div className="pt-2">
          <Button onClick={save} disabled={saving} data-testid="ctrl-save">{saving ? "Guardando..." : "Guardar cambios"}</Button>
        </div>
      </div>
    </div>
  );
}
